import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Zap, X, Loader2 } from "lucide-react";

const ORDER_TYPES = [
  { v: "oco", label: "OCO — one-cancels-other", hint: "Stop-loss + take-profit as a pair" },
  { v: "trailing_stop", label: "Trailing stop", hint: "Stop that follows price up by X%" },
  { v: "twap", label: "TWAP", hint: "Split into N equal slices over time" },
  { v: "iceberg", label: "Iceberg", hint: "Show only slice_size on the book, refill silently" },
];

export default function AdvancedOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    symbol: "AAPL", asset_type: "stock", side: "buy", quantity: 10,
    order_type: "oco", stop_price: "", take_price: "",
    trail_pct: "", slices: "", duration_minutes: "", slice_size: "",
  });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/orders/advanced").then((r) => setOrders(r.data.orders || []))
    .catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (v) => (v === "" ? null : Number(v));

  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        symbol: form.symbol.trim().toUpperCase(),
        asset_type: form.asset_type, side: form.side,
        quantity: Number(form.quantity), order_type: form.order_type,
        stop_price: num(form.stop_price), take_price: num(form.take_price),
        trail_pct: num(form.trail_pct),
        slices: form.slices === "" ? null : parseInt(form.slices),
        duration_minutes: form.duration_minutes === "" ? null : parseInt(form.duration_minutes),
        slice_size: num(form.slice_size),
      };
      // Strip nulls so backend Optional[X] validates cleanly
      Object.keys(body).forEach((k) => { if (body[k] === null) delete body[k]; });
      await api.post("/orders/advanced", body);
      toast.success(`${form.order_type.toUpperCase()} order registered`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const cancel = async (id) => {
    try { await api.delete(`/orders/advanced/${id}`); load(); toast.success("Cancelled"); }
    catch { toast.error("Cancel failed"); }
  };

  return (
    <div className="space-y-6" data-testid="advorders-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Execution</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mt-1 flex items-center gap-2">
          <Zap className="w-7 h-7" /> Advanced Orders
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          OCO pairs, trailing stops, TWAP schedules, and iceberg orders. Fills routed by the same paper/live engine.
        </p>
      </div>

      {/* Ticket */}
      <Card className="p-5 rounded-2xl" data-testid="advorder-ticket">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">Symbol</Label>
            <Input value={form.symbol} onChange={(e) => set("symbol", e.target.value.toUpperCase())}
                   data-testid="ao-symbol" className="rounded-lg" />
          </div>
          <div>
            <Label className="text-xs">Type</Label>
            <select value={form.asset_type} onChange={(e) => set("asset_type", e.target.value)}
                    data-testid="ao-asset" className="w-full h-10 rounded-lg border border-input bg-transparent px-3 text-sm">
              <option value="stock">stock</option><option value="crypto">crypto</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Side</Label>
            <select value={form.side} onChange={(e) => set("side", e.target.value)}
                    data-testid="ao-side" className="w-full h-10 rounded-lg border border-input bg-transparent px-3 text-sm">
              <option value="buy">buy</option><option value="sell">sell</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">Quantity</Label>
            <Input type="number" step="any" value={form.quantity} onChange={(e) => set("quantity", e.target.value)}
                   data-testid="ao-qty" className="rounded-lg" />
          </div>
          <div>
            <Label className="text-xs">Order type</Label>
            <select value={form.order_type} onChange={(e) => set("order_type", e.target.value)}
                    data-testid="ao-type" className="w-full h-10 rounded-lg border border-input bg-transparent px-3 text-sm">
              {ORDER_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </div>
        </div>

        {/* Conditional fields */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
          {form.order_type === "oco" && (<>
            <NF label="Stop price" v={form.stop_price} on={(v) => set("stop_price", v)} testid="ao-stop" />
            <NF label="Take price" v={form.take_price} on={(v) => set("take_price", v)} testid="ao-take" />
          </>)}
          {form.order_type === "trailing_stop" && (
            <NF label="Trail %" v={form.trail_pct} on={(v) => set("trail_pct", v)} testid="ao-trail" step="0.1" />
          )}
          {form.order_type === "twap" && (<>
            <NF label="Slices" v={form.slices} on={(v) => set("slices", v)} testid="ao-slices" step="1" />
            <NF label="Duration (min)" v={form.duration_minutes} on={(v) => set("duration_minutes", v)} testid="ao-duration" step="1" />
          </>)}
          {form.order_type === "iceberg" && (<>
            <NF label="Slice size" v={form.slice_size} on={(v) => set("slice_size", v)} testid="ao-slicesz" />
            <NF label="Slices" v={form.slices} on={(v) => set("slices", v)} testid="ao-slices2" step="1" />
          </>)}
        </div>

        <div className="mt-4 flex justify-between items-center">
          <div className="text-xs text-muted-foreground">
            {ORDER_TYPES.find((t) => t.v === form.order_type)?.hint}
          </div>
          <Button onClick={submit} disabled={saving} data-testid="ao-submit" className="rounded-full">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
            Register order
          </Button>
        </div>
      </Card>

      {/* Active list */}
      <Card className="p-5 rounded-2xl" data-testid="advorder-list">
        <div className="text-lg font-display font-semibold mb-4">Active orders</div>
        {loading ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
        ) : orders.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="advorder-empty">
            No advanced orders yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left py-2 font-medium">Type</th>
                  <th className="text-left py-2 font-medium">Symbol</th>
                  <th className="text-left py-2 font-medium">Side</th>
                  <th className="text-right py-2 font-medium">Qty</th>
                  <th className="text-left py-2 font-medium">Params</th>
                  <th className="text-left py-2 font-medium">Status</th>
                  <th className="text-right py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-border/60" data-testid={`advorder-row-${o.id}`}>
                    <td className="py-2 uppercase text-xs tracking-widest">{o.order_type}</td>
                    <td className="py-2 font-medium">{o.symbol}</td>
                    <td className="py-2">{o.side}</td>
                    <td className="py-2 text-right font-mono-tab">{o.quantity}</td>
                    <td className="py-2 text-xs text-muted-foreground">
                      {o.stop_price && <>stop {o.stop_price} </>}
                      {o.take_price && <>take {o.take_price} </>}
                      {o.trail_pct && <>trail {o.trail_pct}% </>}
                      {o.slices && <>{o.slices} slices </>}
                      {o.duration_minutes && <>{o.duration_minutes}m </>}
                      {o.slice_size && <>slice {o.slice_size} </>}
                    </td>
                    <td className="py-2">
                      <Badge variant={o.status === "active" ? "secondary" : "outline"} className="rounded-full text-[10px]">{o.status}</Badge>
                    </td>
                    <td className="py-2 text-right">
                      {o.status === "active" && (
                        <Button size="sm" variant="ghost" onClick={() => cancel(o.id)}
                                data-testid={`advorder-cancel-${o.id}`} className="text-[hsl(var(--loss))]">
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

const NF = ({ label, v, on, testid, step = "any" }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input type="number" step={step} value={v} onChange={(e) => on(e.target.value)}
           data-testid={testid} className="rounded-lg" />
  </div>
);
